/**
 * Day plan rules for one booking (coordination workbench, part 2). Pure functions.
 *
 * Inputs are what already exists: the booking row, logistics_questionnaire.answers (the client's own
 * form: coverage times, getting-ready, first look, locations with lat/lng from Places), and
 * bookings.mileage_calculation_details written by the calculate-mileage-fee function (routed leg
 * miles) or its refusal. Nothing here is stored.
 */

const EARTH_RADIUS_MILES = 3958.7613

const num = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
export const isCoord = (p) => Boolean(p) && num(p.lat) !== null && num(p.lng) !== null
const round1 = (n) => Math.round(n * 10) / 10

/** Straight-line miles between two {lat, lng}; null when either side has no coordinates. */
export function haversineMiles(a, b) {
  if (!isCoord(a) || !isCoord(b)) return null
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(num(b.lat) - num(a.lat))
  const dLng = toRad(num(b.lng) - num(a.lng))
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(num(a.lat))) * Math.cos(toRad(num(b.lat))) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(s))
}

/** '13:00:00' | '13:00' | '9:5' → '1:00 PM'; strings already carrying AM/PM pass through; anything else returns '' . */
export function formatClock(t) {
  if (t === null || t === undefined) return ''
  const s = String(t).trim()
  if (!s) return ''
  if (/[ap]\.?m\.?$/i.test(s)) return s.replace(/\s+/g, ' ').toUpperCase()
  const m = s.match(/^(\d{1,2}):(\d{1,2})/)
  if (!m) return s
  const h = Number(m[1]), mins = Number(m[2])
  if (h > 23 || mins > 59) return s
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(mins).padStart(2, '0')} ${suffix}`
}

/** Minutes since midnight for sorting; null when the value is not a clock time. */
export function clockMinutes(t) {
  const s = String(t || '').trim()
  let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap]\.?m\.?)?$/i)
  if (!m) return null
  let h = Number(m[1])
  const mins = Number(m[2])
  const ap = m[3] ? m[3].toLowerCase().replace(/\./g, '') : null
  if (ap === 'pm' && h < 12) h += 12
  if (ap === 'am' && h === 12) h = 0
  if (h > 23 || mins > 59) return null
  return h * 60 + mins
}

/**
 * Ordered stops for the day and the legs between them.
 * Leg miles come from the routed calculation when it exists and matches the stop count; otherwise a
 * straight-line estimate, labelled as such; null when a stop has no coordinates (typed, unverified).
 */
export function planLocations(answers = {}, details = null) {
  const raw = Array.isArray(answers?.locations) ? answers.locations : []
  const locations = raw
    .filter((l) => l && (l.address || l.label))
    .map((l, i) => ({
      order: i + 1,
      label: l.label || `Stop ${i + 1}`,
      address: l.address || l.label,
      lat: num(l.lat),
      lng: num(l.lng),
      verified: isCoord(l)
    }))
  const legsMiles = details && !details.refused && Array.isArray(details.legs_miles) ? details.legs_miles : null
  const routed = Boolean(legsMiles && legsMiles.length === Math.max(0, locations.length - 1) && locations.length > 1)
  const legs = locations.slice(1).map((to, i) => {
    const from = locations[i]
    const miles = routed ? num(legsMiles[i]) : haversineMiles(from, to)
    return {
      from: from.label,
      to: to.label,
      miles: miles === null ? null : round1(miles),
      source: routed ? 'routed' : miles === null ? 'unknown' : 'estimate'
    }
  })
  const totalMiles = legs.length && legs.every((l) => l.miles !== null) ? round1(legs.reduce((s, l) => s + l.miles, 0)) : null
  return {
    locations,
    legs,
    totalMiles,
    routed,
    unverified: locations.filter((l) => !l.verified).length,
    refused: details?.refused ? { reason: details.reason || 'refused', detail: details.detail || '' } : null,
    calculatedAt: details?.calculated_at || null
  }
}

/** Coverage hours: the client's logistics answer first, then the booking, then the package snapshot. */
export function coverageHours(booking = {}, answers = {}) {
  const p = booking.personalization_data || {}
  const candidates = [answers.coverage_hours, booking.hours_booked, p.package?.hoursBooked, p.schedule?.hours, p.package?.hours]
  for (const c of candidates) {
    const n = num(c)
    if (n !== null && n > 0) return n
  }
  return null
}

/**
 * The day in order: getting ready, coverage start, first look, coverage end. Each entry carries the
 * raw time, a display time, a label and the place. Entries without a time sit after coverage start.
 */
export function buildTimeline(booking = {}, answers = {}) {
  const firstStop = Array.isArray(answers.locations) && answers.locations[0]
    ? (answers.locations[0].label || answers.locations[0].address || null)
    : (booking.venue_name || null)
  const start = answers.coverage_start_time || booking.event_time || null
  const end = answers.coverage_end_time || booking.event_end_time || null
  const entries = []
  if (answers.getting_ready_time || answers.getting_ready_location) {
    entries.push({ key: 'getting_ready', time: answers.getting_ready_time || null, label: 'Getting ready', place: answers.getting_ready_location || null, note: answers.getting_ready_notes || null })
  }
  if (start) entries.push({ key: 'coverage_start', time: start, label: 'Coverage starts', place: firstStop, note: null })
  if (answers.first_look_choice === 'yes' || answers.first_look_location) {
    entries.push({ key: 'first_look', time: null, label: 'First look', place: answers.first_look_location || null, note: null })
  }
  if (end) entries.push({ key: 'coverage_end', time: end, label: 'Coverage ends', place: null, note: null })

  const startMin = clockMinutes(start)
  const withOrder = entries.map((e, i) => ({ ...e, _i: i, _m: clockMinutes(e.time) ?? (e.key === 'first_look' && startMin !== null ? startMin + 0.5 : null) }))
  withOrder.sort((a, b) => {
    if (a._m === null && b._m === null) return a._i - b._i
    if (a._m === null) return 1
    if (b._m === null) return -1
    return a._m - b._m || a._i - b._i
  })
  return withOrder.map(({ _i, _m, ...e }) => ({ ...e, display: formatClock(e.time) }))
}

/** Straight-line miles from a talent's home (photographers.latitude/longitude) to a stop; null when either is unknown. */
export function talentDistanceMiles(talent, stop) {
  if (!talent || !stop) return null
  const d = haversineMiles({ lat: talent.latitude, lng: talent.longitude }, stop)
  return d === null ? null : round1(d)
}

/** Validate one upload link for the save box: empty clears it; otherwise it must be an http(s) URL. */
export function normalizeUploadLink(value) {
  const s = String(value || '').trim()
  if (!s) return { ok: true, value: null }
  if (s.length > 2000) return { ok: false, error: 'Link is too long' }
  try {
    const u = new URL(s)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return { ok: false, error: 'Link must start with http:// or https://' }
    return { ok: true, value: u.toString() }
  } catch {
    return { ok: false, error: 'Not a valid link' }
  }
}

export const UPLOAD_LINK_FIELDS = [
  { key: 'photos', column: 'photos_upload_link', label: 'Photos' },
  { key: 'videos', column: 'videos_upload_link', label: 'Video' },
  { key: 'raws', column: 'raws_upload_link', label: 'RAWs' }
]
